import { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';

/**
 * Autocomplete Component
 *
 * Multi-select autocomplete dropdown with search functionality.
 * Based on Preline UI patterns with custom implementation.
 *
 * @param {Object} props
 * @param {string} props.label - Label for the input
 * @param {Array} props.options - Array of {value, label} objects
 * @param {Array} props.value - Array of selected values
 * @param {Function} props.onChange - Change handler receiving array of selected values
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.multiple - Enable multiple selection (default: true)
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.serverSideFilter - Options already match the typed text (server search): skip local filtering
 * @param {boolean} props.hasMore - More options can be loaded when the list is scrolled to the end
 * @param {Function} props.onLoadMore - Called when the end of the list is reached
 * @param {boolean} props.isLoadingMore - A next page is loading
 */
export function Autocomplete({
  label,
  options = [],
  value = [],
  onChange,
  placeholder = 'Select...',
  multiple = true,
  className = '',
  onSearchTextChange = null,
  isLoading = false,
  emptyMessage = 'No options found',
  loadingMessage = 'Searching...',
  serverSideFilter = false,
  hasMore = false,
  onLoadMore = null,
  isLoadingMore = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter options based on search text (memoized for performance)
  const filteredOptions = useMemo(() => {
    if (serverSideFilter) return options;
    return options.filter(option => {
      const label = String(option.label || '');
      const search = String(searchText || '');
      return label.toLowerCase().includes(search.toLowerCase());
    });
  }, [options, searchText, serverSideFilter]);

  // Infinite scroll: ask for the next page when the list is near its end
  const handleListScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (hasMore && !isLoadingMore && onLoadMore && scrollHeight - scrollTop - clientHeight < 48) {
      onLoadMore();
    }
  };

  // Handle option toggle
  const handleToggleOption = (optionValue) => {
    if (multiple) {
      const newValue = value.includes(optionValue)
        ? value.filter(v => v !== optionValue)
        : [...value, optionValue];
      onChange(newValue);
      setSearchText('');
    } else {
      onChange([optionValue]);
      setSearchText('');
      setIsOpen(false);
    }
  };

  // Remove selected item
  const handleRemove = (valueToRemove) => {
    onChange(value.filter(v => v !== valueToRemove));
  };

  // Get label for a value
  const getLabel = (val) => {
    const option = options.find(opt => opt.value === val);
    return option ? option.label : val;
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-muted mb-2">
          {label}
        </label>
      )}

      {/* Input Area */}
      <div
        className="min-h-[42px] py-2 px-3 border border-border-default rounded-lg bg-white cursor-pointer flex flex-wrap gap-2 items-center hover:border-border-strong focus-within:border-brand focus-within:ring-1 focus-within:ring-brand transition-all"
        onClick={() => {
          setIsOpen(true);
          inputRef.current?.focus();
        }}
      >
        {/* Selected Tags */}
        {value.map((val) => (
          <span
            key={val}
            className="inline-flex items-center gap-x-1 py-1 px-2 rounded-full text-xs font-medium bg-brand/10 text-brand"
          >
            {getLabel(val)}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove(val);
              }}
              className="hover:bg-brand/20 rounded-full p-0.5 cursor-pointer"
            >
              <Icon icon="lucide:x" className="size-3" />
            </button>
          </span>
        ))}

        {/* Search Input */}
        <input
          ref={inputRef}
          type="text"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            if (onSearchTextChange) onSearchTextChange(e.target.value);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={value.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[120px] outline-none text-sm border-none focus:ring-0 p-0"
        />

        {/* Dropdown Icon */}
        <Icon
          icon="lucide:chevron-down"
          className={`size-4 icon-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute z-50 w-full mt-1 bg-surface border border-border-default rounded-lg shadow-lg max-h-60 overflow-y-auto"
          onScroll={handleListScroll}
        >
          {isLoading ? (
            <div className="py-3 px-3 flex items-center gap-2 text-sm text-muted">
              <Icon icon="lucide:loader-circle" className="size-4 animate-spin" />
              <span>{loadingMessage}</span>
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="py-2 px-3 text-sm text-muted">
              {emptyMessage}
            </div>
          ) : (
            <ul className="py-1">
              {filteredOptions.map((option) => {
                const isSelected = value.includes(option.value);
                return (
                  <li key={option.value}>
                    <button
                      type="button"
                      onClick={() => handleToggleOption(option.value)}
                      className={`
                        w-full text-left py-2 px-3 text-sm hover:bg-hover-muted
                        flex items-center gap-2 cursor-pointer
                        ${isSelected ? 'bg-brand/5 text-brand' : 'text-muted'}
                      `}
                    >
                      {multiple && (
                        <div className={`
                          size-4 border rounded flex items-center justify-center
                          ${isSelected ? 'bg-primary border-primary' : 'border-border-strong'}
                        `}>
                          {isSelected && (
                            <Icon icon="lucide:check" className="size-3 text-white" />
                          )}
                        </div>
                      )}
                      <span>{option.label}</span>
                    </button>
                  </li>
                );
              })}
              {isLoadingMore && (
                <li className="py-2 px-3 flex items-center gap-2 text-sm text-muted">
                  <Icon icon="lucide:loader-circle" className="size-4 animate-spin" />
                  <span>{loadingMessage}</span>
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

Autocomplete.propTypes = {
  label: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  multiple: PropTypes.bool,
  className: PropTypes.string,
  onSearchTextChange: PropTypes.func,
  isLoading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  loadingMessage: PropTypes.string,
  serverSideFilter: PropTypes.bool,
  hasMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  isLoadingMore: PropTypes.bool,
};
